import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Receipt, Package, ChatCircleText } from "@phosphor-icons/react";

export default function Account() {
  const { user, setAuthOpen } = useAuth();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");
  const nav = useNavigate();

  useEffect(() => {
    if (user === false) { setAuthOpen(true); nav("/"); return; }
    if (!user) return;
    Promise.all([
      api.get("/orders").then((r) => setOrders(r.data || [])).catch(() => {}),
      api.get("/quotes").then((r) => setQuotes(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [user, setAuthOpen, nav]);

  if (!user || user === false) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-12" data-testid="account-page">
      <h1 className="font-display font-black text-4xl tracking-tight mb-1">Mon compte</h1>
      <p className="text-zinc-500 mb-8">{user.email}</p>

      <div className="flex border-b-2 border-black mb-6">
        <button onClick={() => setTab("orders")} className={`tab-btn flex items-center gap-2 ${tab==="orders"?"active":""}`} data-testid="tab-orders">
          <Receipt size={16} weight="bold" /> Commandes ({orders.length})
        </button>
        <button onClick={() => setTab("quotes")} className={`tab-btn flex items-center gap-2 ${tab==="quotes"?"active":""}`} data-testid="tab-quotes">
          <ChatCircleText size={16} weight="bold" /> Devis ({quotes.length})
        </button>
      </div>

      {loading && <div className="spinner" />}

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 && (
            <div className="border-2 border-black p-10 text-center text-zinc-500" data-testid="no-orders">
              Aucune commande pour le moment.
            </div>
          )}
          {orders.map((o) => (
            <div key={o.id} className="border-2 border-black p-5 grid md:grid-cols-4 gap-4" data-testid={`order-${o.id}`}>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Commande</div>
                <div className="mono text-xs truncate">{o.id.slice(0, 8)}…</div>
                <div className="text-xs text-zinc-500 mt-1">{new Date(o.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Statut</div>
                <div className={`inline-block mt-1 px-2 py-1 text-xs font-bold border-2 ${o.payment_status === "paid" ? "border-[#00E676] text-[#00A050]" : "border-zinc-400 text-zinc-500"}`}>
                  {o.payment_status === "paid" ? "Payée" : (o.payment_status || "En attente")}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Articles</div>
                <div className="flex items-center gap-1 mt-1"><Package size={14} weight="bold" />{o.items?.length || 0}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Total</div>
                <div className="font-display font-black text-2xl text-[#E60000]">{o.amount?.toFixed(2)} €</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "quotes" && (
        <div className="space-y-4">
          {quotes.length === 0 && (
            <div className="border-2 border-black p-10 text-center text-zinc-500" data-testid="no-quotes">
              Aucune demande de devis.
            </div>
          )}
          {quotes.map((q) => (
            <div key={q.id} className="border-2 border-black p-4 flex gap-4" data-testid={`quote-${q.id}`}>
              {q.image && <img src={q.image} alt="" className="w-20 h-20 object-cover border border-zinc-200 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                {q.equipementier && <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{q.equipementier}</div>}
                <div className="font-display font-bold truncate">{q.designation}</div>
                <div className="flex flex-wrap gap-3 text-xs text-zinc-500 mt-1">
                  {q.reference && <span className="mono">Réf. {q.reference}</span>}
                  <span>Qté : <strong>{q.quantity}</strong></span>
                  <span>{new Date(q.created_at).toLocaleString("fr-FR")}</span>
                </div>
              </div>
              <div className="text-right">
                <div className={`inline-block px-2 py-1 text-xs font-bold border-2 ${q.status==="responded"?"border-[#00E676] text-[#00A050]":"border-amber-400 text-amber-600"}`}>
                  {q.status === "responded" ? "Répondu" : "En attente"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
